<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { getCompanyLogoUploadUrl } from '@/api/company/profile'

const props = defineProps<{
  currentLogo?: string | null
}>()

const emits = defineEmits<{
  (e: 'uploaded', payload: { url: string; fileType: string }): void
  (e: 'removed'): void
  (e: 'close'): void
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const errorMessage = ref<string | null>(null)
const preview = ref<string | null>(props.currentLogo ?? null)

const MAX_SIZE = 2 * 1024 * 1024 // 2MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

watch(
  () => props.currentLogo,
  (value) => {
    if (!isUploading.value) {
      preview.value = value ?? null
    }
  }
)

const hasPreview = computed(() => !!preview.value)

function openFilePicker() {
  fileInputRef.value?.click()
}

function onDragOver() {
  isDragging.value = true
}

function onDragLeave() {
  isDragging.value = false
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) handleFile(file)
}

function onFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) handleFile(file)
}

async function handleFile(file: File) {
  errorMessage.value = null

  if (!ALLOWED_TYPES.includes(file.type)) {
    errorMessage.value = 'Unsupported file type (only PNG, JPG, WEBP allowed).'
    return
  }

  if (file.size > MAX_SIZE) {
    errorMessage.value = 'File too large (max 2MB).'
    return
  }

  try {
    isUploading.value = true
    const { data } = await getCompanyLogoUploadUrl(file.type)

    await axios.put(data.uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    })

    const url = `${data.publicUrl}?t=${Date.now()}`
    preview.value = url
    emits('uploaded', { url, fileType: file.type })
  } catch (error) {
    console.error('Failed to upload company logo', error)
    errorMessage.value = 'Upload failed. Please try again.'
  } finally {
    isUploading.value = false
  }
}

function handleRemove() {
  preview.value = null
  emits('removed')
  if (fileInputRef.value) fileInputRef.value.value = ''
}

function handleClose() {
  emits('close')
}
</script>

<template>
  <div class="rounded-lg border border-dashed border-muted bg-muted/10 p-4">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-medium">Upload New Logo</h3>
        <p class="text-xs text-muted-foreground">
          PNG, JPG, or WEBP • Max 2MB
        </p>
      </div>
      <button
        type="button"
        class="text-xs text-muted-foreground hover:text-foreground"
        @click="handleClose"
      >
        Cancel
      </button>
    </div>

    <div
      class="relative flex w-full flex-col items-center justify-center rounded-md border-2 border-dashed bg-background p-4 text-center transition"
      :class="isDragging ? 'border-brand/60' : 'border-muted-foreground/30'"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div
        v-if="hasPreview"
        class="relative flex w-full items-center justify-center overflow-hidden rounded-md border bg-white"
        style="max-height: 200px"
      >
        <img :src="preview" alt="Company logo preview" class="max-h-[180px] max-w-full object-contain" />
        <button
          type="button"
          class="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/80"
          @click="handleRemove"
          aria-label="Remove logo"
        >
          ✕
        </button>
      </div>

      <div v-else class="flex flex-col items-center gap-2 py-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-muted-foreground/60" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-9-12v12m0 0L7.5 13.5m4.5 2.25L16.5 13.5"/>
        </svg>
        <p class="text-sm text-muted-foreground">
          <span class="font-medium text-foreground">Drop</span> your logo here or
          <button type="button" class="underline" @click="openFilePicker">browse</button>
        </p>
      </div>

      <div
        v-if="isUploading"
        class="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px]"
      >
        <svg class="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" />
          <path class="opacity-75" d="M4 12a8 8 0 018-8" stroke="currentColor" />
        </svg>
        <span class="ml-2 text-sm">Uploading…</span>
      </div>

      <input
        ref="fileInputRef"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        class="hidden"
        @change="onFileChange"
      />
    </div>

    <p v-if="errorMessage" class="mt-2 text-xs text-red-600">
      {{ errorMessage }}
    </p>
    <p v-else class="mt-2 text-xs text-muted-foreground">
      Recommended square logo (e.g., 500×500). Non-square images will be letterboxed (not cropped).
    </p>
  </div>
</template>
